import SecurityIcon from "@mui/icons-material/Security";
import { Box, Typography, Avatar, Chip, Link, Tooltip } from "@mui/material";
import type {
  InboundMessage as ApiInboundMessage,
  OutboundMessage as ApiOutboundMessage,
} from "@rubi/api-generated/clientRs";
import { colors } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { useDevEnvironment } from "@/contexts/DevContext";

type Message =
  | (ApiOutboundMessage & { type: "outbound"; timestamp: string })
  | (ApiInboundMessage & { type: "inbound"; timestamp: string });

interface ChatlineProps {
  message: Message;
  displayName: string;
  senderName?: string;
  senderInitial?: string;
  showDateSeparator?: boolean;
  dateLabel?: string;
  orgId?: string;
}

const renderMessageContent = (content: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = content.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <Link
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "primary.main", textDecoration: "none" }}
        >
          {part}
        </Link>
      );
    }
    return part;
  });
};

const formatTime = (timestamp: string) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDateTime = (timestamp: string) => {
  const date = new Date(timestamp);
  const dateStr = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const timeStr = formatTime(timestamp);
  return `${dateStr} ${timeStr}`;
};

const Chatline = ({
  message,
  displayName,
  senderName,
  senderInitial = "U",
  showDateSeparator = false,
  dateLabel = "",
  orgId,
}: ChatlineProps) => {
  const { t } = useTranslation("message");
  const { isDevEnv } = useDevEnvironment();
  const isOutbound = message.type === "outbound";
  const outboundMsg = isOutbound
    ? (message as ApiOutboundMessage & { type: "outbound"; timestamp: string })
    : null;
  const inboundMsg = !isOutbound
    ? (message as ApiInboundMessage & { type: "inbound"; timestamp: string })
    : null;
  const finalSenderName =
    senderName || (isOutbound ? t("chatline.you") : displayName);

  const serviceConfig = orgId ? getServiceConfig(orgId, isDevEnv) : null;
  const logoURL = serviceConfig?.logoURL;
  return (
    <Box>
      {/* Date Separator */}
      {showDateSeparator && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            my: 3,
            position: "relative",
          }}
        >
          {/* Horizontal line */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: 0,
              right: 0,
              height: "1px",
              bgcolor: "divider",
              zIndex: 1,
            }}
          />
          {/* Date chip centered on the line */}
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              bgcolor: "background.paper",
              px: 2,
              mx: "auto",
            }}
          >
            <Chip
              label={dateLabel}
              sx={{
                bgcolor: "background.paper",
                fontWeight: 500,
                fontSize: "0.875rem",
                border: "1px solid var(--divider)",
                borderRadius: "16px",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Message Card */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          py: 2, // Equal padding top and bottom
          borderRadius: 1,
          transition: "background-color 0.2s ease",
          mx: -3, // Extend to full width by negating parent padding
          px: 3, // Add back the padding for content
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        {/* Avatar or Logo */}
        {isOutbound && logoURL ? (
          <Box
            component="img"
            src={logoURL}
            alt={t("chatline.agencyLogo")}
            loading="lazy"
            sx={{
              width: 50,
              height: 50,
              borderRadius: 1,
              objectFit: "contain",
              imageRendering: "auto",
            }}
          />
        ) : (
          <Avatar
            sx={{
              bgcolor: isOutbound ? colors.primary.main : "primary.main",
              width: 50,
              height: 50,
              "& img": {
                imageRendering: "auto",
                filter: "contrast(1) brightness(1)",
              },
            }}
          >
            {isOutbound ? <SecurityIcon /> : senderInitial}
          </Avatar>
        )}
        <div>
          <div>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: "text.primary",
              }}
            >
              {finalSenderName}{" "}
              <Typography
                component="span"
                variant="body2"
                sx={{ color: "text.secondary", fontWeight: 400 }}
              >
                | {formatDateTime(message.timestamp)}
              </Typography>
              {isOutbound && (
                <Tooltip
                  title={
                    outboundMsg?.time_delivered
                      ? t("chatline.delivered")
                      : t("chatline.reachedServer")
                  }
                  arrow
                >
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 400 }}
                  >
                    {outboundMsg?.time_delivered ? " ✓✓" : " ✓"}
                  </Typography>
                </Tooltip>
              )}
            </Typography>
          </div>
          <div>
            {/* Message Content */}
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
              }}
            >
              {renderMessageContent(
                isOutbound
                  ? outboundMsg?.content || ""
                  : inboundMsg?.content || "",
              )}
            </Typography>
          </div>
        </div>

        {/* Note: Translated content feature removed as it's not in the current API */}
      </Box>
    </Box>
  );
};

export default Chatline;
